import type {ChatMessage, ResultGroup} from '@constructor-io/constructorio-ui-asa';
import type {TimingMetadata} from '../timing';

export interface TweResultGroup extends ResultGroup {
  /** Unmodified ASA fields; presentation never overwrites tracking metadata. */
  originalTitle?: string;
  description?: string;
}
export type AssistantBlock =
  | {id: string; type: 'message'; text: string}
  | {id: string; type: 'search_result'; groupIndex: number}
  | {id: string; type: 'follow_up_refinement'; question: string; options: string[]}
  | {id: string; type: 'follow_up_questions'; questions: string[]};
export interface TweChatMessage extends ChatMessage, TimingMetadata {
  groups?: TweResultGroup[];
  /** Absent only on v1 conversations written before ordered rendering. */
  blocks?: AssistantBlock[];
  followUpQuestions?: string[];
  renderAttempt?: number;
}

/** Keep the original aggregate callback contract; use blocks for visual prose. */
export function appendBlock(message: TweChatMessage, block: Omit<AssistantBlock, 'id'> & Record<string, unknown>): AssistantBlock[] {
  return [...(message.blocks || []), {id: `block-${message.blocks?.length || 0}`, ...block} as AssistantBlock];
}

function record(value: unknown): value is Record<string, unknown> { return Boolean(value) && typeof value === 'object' && !Array.isArray(value); }
function strings(value: unknown): value is string[] { return Array.isArray(value) && value.every(item => typeof item === 'string'); }
export function validBlocks(value: unknown, groups: TweResultGroup[] = []): value is AssistantBlock[] {
  if (!Array.isArray(value)) return false;
  const ids = new Set<string>(); const indexes = new Set<number>();
  for (const block of value) {
    if (!record(block) || typeof block.id !== 'string' || ids.has(block.id)) return false;
    ids.add(block.id);
    if (block.type === 'message') { if (typeof block.text !== 'string') return false; }
    else if (block.type === 'search_result') {
      if (!Number.isInteger(block.groupIndex) || (block.groupIndex as number) < 0 || !groups[block.groupIndex as number] || indexes.has(block.groupIndex as number)) return false;
      indexes.add(block.groupIndex as number);
    } else if (block.type === 'follow_up_refinement') {
      if (typeof block.question !== 'string' || !block.question.trim() || !strings(block.options) || !block.options.length || block.options.some(option => !option.trim())) return false;
    } else if (block.type === 'follow_up_questions') {
      if (!strings(block.questions) || !block.questions.length || block.questions.some(question => !question.trim())) return false;
    } else return false;
  }
  return indexes.size === groups.length;
}

/** Historical event order cannot be inferred from the v1 aggregate. Preserve its
 * old text → groups → controls layout instead of inventing product associations. */
export function displayBlocks(message: TweChatMessage): AssistantBlock[] {
  if (message.blocks && validBlocks(message.blocks, message.groups)) return message.blocks;
  const blocks: AssistantBlock[] = [];
  if (message.text?.trim()) blocks.push({id: 'legacy-text', type: 'message', text: message.text});
  message.groups?.forEach((_group, groupIndex) => blocks.push({id: `legacy-group-${groupIndex}`, type: 'search_result', groupIndex}));
  if (message.refinement?.question?.trim() && message.refinement.options?.some(option => option.trim())) blocks.push({id: 'legacy-refinement', type: 'follow_up_refinement', ...message.refinement});
  if (message.followUpQuestions?.length) blocks.push({id: 'legacy-questions', type: 'follow_up_questions', questions: message.followUpQuestions});
  return blocks;
}

const normalizeName = (value: string) => value.normalize('NFKC').replace(/[‘’]/g, "'").replace(/[“”]/g, '"').replace(/\s+/g, ' ').trim().toLowerCase();
const normalizeVisibleText = (value: string) => value
  .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
  .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
  .replace(/https?:\/\/\S+/gi, ' ')
  .replace(/<[^>]*>/g, ' ')
  .normalize('NFKC')
  .replace(/[‘’]/g, "'")
  .replace(/[“”]/g, '"')
  .replace(/[^\p{L}\p{N}]+/gu, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();

function singletonNames(group: TweResultGroup): string[] {
  if (group.searchResults.length !== 1) return [];
  const result = group.searchResults[0] as Record<string, unknown>;
  const data = record(result.data) ? result.data : {};
  const candidates = [result.value, data.productDisplayName, data.name]
    .filter((name): name is string => typeof name === 'string' && Boolean(name.trim()))
    .flatMap(name => [name, name.split(' / ')[0]])
    .map(normalizeName);
  return [...new Set(candidates)];
}

/** Presentation-only pairing guard: a preceding message must visibly contain a
 * complete known name for the sole product. Links contribute their label but
 * not their URL, so a product slug alone cannot create a false association. */
export function messageIdentifiesSingletonProduct(text: string, group: TweResultGroup): boolean {
  const visible = ` ${normalizeVisibleText(text)} `;
  return singletonNames(group).some(name => {
    const normalized = normalizeVisibleText(name);
    return normalized.length >= 4 && visible.includes(` ${normalized} `);
  });
}

/** Preserve the established title-backed row when an explanation uses a
 * substantial leading portion of the first product's name (e.g. omitting
 * "Bourbon"). This is NOT evidence for the stricter standalone pairing rule. */
export function messageMentionsProductNamePrefix(text: string, group: TweResultGroup): boolean {
  const visible = ` ${normalizeVisibleText(text)} `;
  return singletonNames(group).some(name => {
    const tokens = normalizeVisibleText(name).split(' ');
    if (tokens.length < 4) return false;
    const prefix = tokens.slice(0, 3).join(' ');
    return prefix.length >= 12 && visible.includes(` ${prefix} `);
  });
}

export function originalGroupTitle(group: TweResultGroup): string {
  return typeof group.originalTitle === 'string' ? group.originalTitle : group.group?.display_name || '';
}
export function numberedSingletonOrdinal(group: TweResultGroup): number | undefined {
  if (group.searchResults.length !== 1) return undefined;
  const match = originalGroupTitle(group).trim().match(/^(\d+)[.)]\s+(.+)$/);
  if (!match || !singletonNames(group).includes(normalizeName(match[2]))) return undefined;
  const ordinal = Number(match[1]);
  return Number.isSafeInteger(ordinal) && ordinal > 0 ? ordinal : undefined;
}
/** A category is evidence for the grouped-run fallback only when an explicit
 * supported delimiter separates it from the complete sole-product name. */
export function prefixedSingletonCategory(group: TweResultGroup): string | undefined {
  if (group.searchResults.length !== 1) return undefined;
  const title = originalGroupTitle(group).trim();
  const names = singletonNames(group);
  for (const match of title.matchAll(/(?:\s+[–—-]\s+|:\s*)/g)) {
    const prefix = title.slice(0, match.index).trim();
    const suffix = title.slice(match.index! + match[0].length);
    if (prefix && names.includes(normalizeName(suffix))) return prefix;
  }
  return undefined;
}
export function visualGroupTitle(group: TweResultGroup): string {
  const title = originalGroupTitle(group).trim();
  if (!title || group.searchResults.length !== 1) return title;
  const names = singletonNames(group);
  if (names.includes(normalizeName(title))) return '';
  const ordinal = numberedSingletonOrdinal(group);
  if (ordinal !== undefined) return String(ordinal);
  // Require a known delimiter and an exact normalized sole-product suffix.
  // Never strip partial names, infer categories, or rewrite a themed group.
  return prefixedSingletonCategory(group) || title;
}

export type MultiProductDescription = {ordinal: number; productIndex: number; productLabel: string; descriptionText: string};
const normalizePairingLabel = (value: string) => value.normalize('NFKC').replace(/[‘’]/g,"'").replace(/[“”]/g,'"').replace(/[^\p{L}\p{N}]+/gu,' ').replace(/\s+/g,' ').trim().toLowerCase();

/** All-or-nothing association of one titled numbered message with its original
 * multi-product result. No fuzzy names, positional guesses, or source mutation. */
export function parseMultiProductDescriptions(text: string, group: TweResultGroup): MultiProductDescription[] | undefined {
  if (group.searchResults.length < 2 || !text.trim()) return undefined;
  const lines = text.replace(/\r\n?/g,'\n').trim().split('\n');
  const firstItem = lines.findIndex(line => /^\s*\d+[.)]\s+/.test(line));
  if (firstItem < 1) return undefined;
  const heading = lines.slice(0,firstItem).filter(line=>line.trim());
  if (heading.length !== 1 || !normalizePairingLabel(heading[0]) || normalizePairingLabel(heading[0]) !== normalizePairingLabel(originalGroupTitle(group))) return undefined;
  const candidates = group.searchResults.map(result => {
    const source = result as Record<string,unknown>;
    const data = record(source.data) ? source.data : {};
    return new Set([source.value,data.name,data.productDisplayName]
      .filter((name):name is string=>typeof name==='string'&&Boolean(name.trim()))
      .flatMap(name=>[name,name.split(' / ')[0]]).map(normalizePairingLabel).filter(Boolean));
  });
  const items: {ordinal:number; text:string}[] = [];
  for (const line of lines.slice(firstItem)) {
    const marker = line.match(/^\s*(\d+)[.)]\s+(.+)$/);
    if (marker) items.push({ordinal:Number(marker[1]),text:marker[2]});
    else {
      // Do not silently absorb a malformed ordinal as explanation copy.
      if (/^\s*\d+[.)](?:\s|$)/.test(line) || !items.length) return undefined;
      items[items.length-1].text += '\n'+line;
    }
  }
  if (items.length !== group.searchResults.length) return undefined;
  const output: MultiProductDescription[] = [];
  for (const [productIndex,item] of items.entries()) {
    if (item.ordinal !== productIndex+1) return undefined;
    const matches: MultiProductDescription[] = [];
    for (const delimiter of item.text.matchAll(/:\s*/g)) {
      const productLabel = item.text.slice(0,delimiter.index).trim();
      const descriptionText = item.text.slice(delimiter.index!+delimiter[0].length).trim();
      const label = normalizePairingLabel(productLabel);
      const matching = candidates.flatMap((names,index)=>names.has(label)?[index]:[]);
      if (descriptionText && matching.length===1 && matching[0]===productIndex) matches.push({ordinal:item.ordinal,productIndex,productLabel,descriptionText});
    }
    if (matches.length!==1) return undefined;
    output.push(matches[0]);
  }
  return output;
}
